import axiosBase from '../../common/axiosBase';
import coreConfig from '../../common/coreConfig';
import { useEffect, useState, useMemo } from 'react';
import ReleaseManagement from '../ReleaseManagementModel';
import { Box, Grid, Typography, Skeleton, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { logger } from '../../App';

const BATCH_SIZE = 3;

export default function ReleaseManagementGrid() {
    const [state, setState] = useState({
        data: [],
        visibleCount: BATCH_SIZE,
        loading: true
    });

    const navigate = useNavigate();

    const fetchData = async () => {
        setState(prevState => ({ ...prevState, loading: true }));
        const { apiConfig } = coreConfig;
        try {
            const response = await axiosBase({
                api: apiConfig.endpoints.dashboard,
                method: apiConfig.reqMethod.POST,
                body: JSON.stringify({
                    where: [
                        { field: 'ShowOnDashboard', operator: 'equals', type: 'boolean', value: true }
                    ],
                    sort: "ModelName ASC"
                })
            });
            if (!response.data.success) {
                setState(prevState => ({ ...prevState, loading: false }));
                return;
            }
            setState(prevState => ({
                ...prevState,
                data: response.data.records
            }));
        } catch (error) {
            logger.error({ error, message: "Error fetching release management data" });
        }
        setState(prevState => ({ ...prevState, loading: false }));
    };

    useEffect(() => {
        fetchData();
    }, []);


    const navigateToForm = ({ id }) => {
        navigate(`/revision/${id}?from=${encodeURIComponent('releasemgmt')}`);
    };

    const onCellClick = ({ cellParams, event, details }) => {
        let action = null;
        if (cellParams.field === 'actions') {
            action = details?.action;
            if (!action) {
                const el = event.target.closest('button');
                if (el) {
                    action = el.dataset.action;
                }
            }
        }
        if (!action) return;
        if (action === 'Edit') {
            navigateToForm({ id: cellParams.id });
        }
        else return false;
    };

    // Only up to visibleCount (max 3 at a time)
    const visibleGrids = useMemo(() => state.data.slice(0, state.visibleCount), [state.data, state.visibleCount]);

    // Whether to show the "Load More" button
    const canLoadMore = useMemo(() => state.visibleCount < state.data.length, [state.visibleCount, state.data.length]);

    return (
        <>
            <Grid container>
                {/* Header Bar */}
                <Grid size={12} sx={{ marginBottom: '10px', marginTop: "10px" }}>
                    <Box display="flex" alignItems="center" justifyContent='space-between' sx={{
                        backgroundColor: "#fff",
                        borderRadius: '5px',
                        padding: '10px'
                    }}>
                        <Typography variant="h5" sx={{ color: 'text.secondary' }}>Release Management</Typography>
                    </Box>
                </Grid>

                {/* Skeleton Loader */}
                {state.loading && (
                    Array.from({ length: BATCH_SIZE }).map(() => (
                        <Grid size={{ xs: 12, md: 12 }} sx={{ marginBottom: '4px', marginTop: "30px" }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{
                                backgroundColor: "#fff",
                                borderRadius: '5px',
                                padding: '10px',
                                mb: 2
                            }}>
                                <Skeleton variant="rectangular" width={220} height={38} />
                            </Box>
                            <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: '5px', mb: 3 }} />
                        </Grid>
                    ))
                )}

                {/* Actual Grids */}
                {!state.loading && visibleGrids.map((item) => (
                    <Grid container key={item.EquipmentId}>
                        <Grid size={{ xs: 12, md: 12 }} sx={{ marginBottom: '4px', marginTop: "30px" }}>
                            <Box display="flex" alignItems="center" justifyContent='space-between' sx={{
                                backgroundColor: "#fff",
                                borderRadius: '5px',
                                padding: '10px'
                            }}>
                                <Typography variant="h5" sx={{ color: 'text.secondary' }}>{item.ModelName}</Typography>
                            </Box>
                        </Grid>
                        <ReleaseManagement.Grid
                            onCellDoubleClickOverride={navigateToForm}
                            onCellClick={onCellClick}
                            title={item.EquipmentName}
                            baseFilters={[{
                                field: "EquipmentId",
                                operator: "equals",
                                value: parseInt(item.EquipmentId),
                                type: "number"
                            }]}
                            key={item.EquipmentId}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Load More Button (centered, outside the Grid) */}
            {!state.loading && canLoadMore && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, mb: 2 }}>
                    <Button
                        variant="text"
                        color="primary"
                        size="large"
                        sx={{
                            color: '#fff',         // font color
                            border: 'none',        // just in case, remove border
                            boxShadow: 'none',     // removes shadow if you want a flat look
                            fontWeight: 600,
                            letterSpacing: '1px'
                        }}
                        onClick={() =>
                            setState(prevState => ({
                                ...prevState,
                                visibleCount: Math.min(prevState.visibleCount + BATCH_SIZE, prevState.data.length)
                            }))
                        }
                    >
                        Load More
                    </Button>
                </Box>
            )}
        </>
    );
}
